import React, { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { useSwipeable } from 'react-swipeable';
import Doc from '@Components/PdfContainer/DocService';
import PdfContainer from '@Components/PdfContainer';
import Utilities from '@Utils/utilities';
import Header from '../components/Header';
import Grid from '../components/Grid';
import RecommendationModal from '../components/RecommendationModal';
import { TGraphicAlt } from '../utils/types';

interface iResult {
    intro: string
    bookingLink: string
    bookingText: string
    kontaktOsTextReplaced: string
    pdfOverskrift: string
    pdfFooter: string
    email?: string
    telefon?: string
    landlinephone?: string
    samtykke: boolean
    blockId: string
    samtykkeLabel: string
    samtykkeTekst: string
    erSagsbehandler: boolean
    sagsbehandlerInfoTekst: string
    anbefalinger: Array<{
        id: number
        title: string
        text: string
        icon: string
        modalOverskrift: string
        modalText: string
		graphic: TGraphicAlt
        modalFooter: string
        buttonLink1: string
        buttonText1: string
        buttonLink2: string
        buttonText2: string
        visAnbefalingSamtykke: boolean
        visRoedBaggrund: boolean
		anbefalingsBaggrund: string
    }>
    senesteBesvarelserList: Array<{
        overskrift: string
        tekst: string
        sorteringsIndex: number
    }>
  }

const ServicetjekResultat = (props: iResult) => {
	const {
		intro,
		bookingLink,
		bookingText,
		kontaktOsTextReplaced,
		anbefalinger,
		senesteBesvarelserList,
		erSagsbehandler,
		sagsbehandlerInfoTekst,
		pdfOverskrift,
		pdfFooter,
		samtykkeLabel,
		samtykkeTekst,
		samtykke,
		email,
		telefon,
		landlinephone,
		blockId
	} = props;

	const [showModal, setShowModal] = useState(false);
	const antalAnbefalinger = Utilities.convertNumberToWord(anbefalinger.length);
	const [modalId, setModalId] = useState(null);
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [positionX, setPositionX] = useState(null);
	const [disableNext, setDisableNext] = useState(false);
	const [recomId, setRecomId] = useState(null);
	const [dotBg, setDotBg] = useState<number>(0);
	const [visiblity, setVisibility] = useState(true);
	const createPdf = () => Doc.createPdf();
	const recommendationsLength = anbefalinger?.length;
	const interval = 297.6;

	const handlers = useSwipeable({
		onSwipedLeft: () => {
			setPositionX(positionX - interval); setRecomId(recomId - 1); setDotBg(dotBg + 1);
		},
		onSwipedRight: () => {
			setPositionX(positionX + interval); setRecomId(recomId + 1); setDotBg(dotBg - 1); setDisableNext(false);
		}
	});

	useEffect(() => {
		const reSize = () => {
			setWindowWidth(window.innerWidth);
			setPositionX(0);
			setDisableNext(false);
			setRecomId(recommendationsLength);
		};

		window.addEventListener('resize', reSize);

		if (recommendationsLength) {
			setRecomId(recommendationsLength);
		}

		return () => {
			window.removeEventListener('resize', reSize);
		};
	}, [setWindowWidth, setDisableNext, windowWidth]);

	const handleCarouselClick = (direction: string) => {
		if (direction === 'next') {
			setPositionX(positionX - interval);
			setRecomId(recomId - 1);
			setDotBg(dotBg + 1);

			if (windowWidth >= 1300 && recomId <= 5) {
				setDisableNext(true);
			} else if (windowWidth <= 1299 && windowWidth > 950 && recomId <= 4) {
				setDisableNext(true);
			} else if (windowWidth <= 950 && windowWidth > 600 && recomId <= 3) {
				setDisableNext(true);
			} else if (windowWidth <= 600 && windowWidth > 300 && recomId <= 2) {
				setDisableNext(true);
			} else if (windowWidth <= 300 && recomId <= 1) {
				setDisableNext(true);
			}
		}

		if (direction === 'prev') {
			setPositionX(positionX + interval);
			setRecomId(recomId + 1);
			setDisableNext(false);
			setDotBg(dotBg - 1);
		}
	};

	const handleModalClick = (id: string) => {
		setModalId(id);
		setShowModal(true);
	};

	const renderPDF = () => {
		const date = new Date();
		const day = date.getDate();
		const month = date.getMonth() + 1;
		const year = date.getFullYear();
		const currentDate = `${day}/${month}/${year}`;

		return (
			<PdfContainer
				createPdf={ createPdf }
				buttonText=""
				successText=""
				autoSave
				apiUrl="/api/WebDokumentationApi/GemDokumentation"
				documentName={ 'Resultat af årligt servicetjek.pdf' }
			>
				<div className="lhp-result">
					<div className="result-pdf">
						<div className="pdf-header">
							<p>{ pdfOverskrift }</p>
							<p>{ currentDate }</p>
						</div>
						<div className="result-header">
							{ intro && (
								<div className="result-intro">
									{ parse(Utilities.strReplaceAll('[antalanbefalinger]', intro, antalAnbefalinger.toString())) }
								</div>
							) }
							{ (
								<div className="result-contact">
									<a href={ 'tel:' + kontaktOsTextReplaced }>
										<p>Kontakt os</p>
										<h2>{ kontaktOsTextReplaced }</h2>
									</a>
								</div>
							) }
						</div>
						<div className="recommendations">
							{ anbefalinger?.map((anbefaling) => (
								<div className="recommendation" key={ anbefaling.id }>
									<div className="recommendation-content">
										<img src={ anbefaling.icon } alt="ikon" className="recommendation-icon" />
										{ anbefaling.title && <h4>{ anbefaling.title }</h4> }
										{ anbefaling.modalOverskrift && <h1>{ anbefaling.modalOverskrift }</h1> }
										{ anbefaling.modalText && parse(anbefaling.modalText) }
										<div className="btn-container">
											{ anbefaling.buttonLink1 && anbefaling.buttonText1 && <a href={ anbefaling.buttonLink1 } className="btn">{ anbefaling.buttonText1 }</a> }
											{ anbefaling.buttonLink2 && anbefaling.buttonText2 && <a href={ anbefaling.buttonLink2 } className="btn btn-secondary">{ anbefaling.buttonText2 }</a> }
										</div>
									</div>
								</div>
							)) }
						</div>
						<div className="modal-footer">
							<p>{ pdfFooter }</p>
						</div>
					</div>
					<div className="page-break" />
					<div className="result-answers-container">
						<h2>Dine svar</h2>
						{ senesteBesvarelserList.map((
							besvarelse: {
							overskrift: string
							tekst: string
							sorteringsIndex: number
						}, i: React.Key) => {
							return (
								<div className="result-answers" key={ i }>
									<h4>{ parse(besvarelse.overskrift) }</h4>
									{ parse(besvarelse.tekst) }
								</div>
							);
						}) }
					</div>
				</div>
			</PdfContainer>
		);
	};

	return (
		<>
			<Header title={ 'Anbefalinger' } baseUrl={ window.location.href } />
			<main className="lhp-main">
				<Grid element="section" className="lhp-result page">
					<div className="lhp-result">
						<div id="portal" />
						<div className="result-header">
							{ intro && (
								<div className="result-intro">
									{ parse(Utilities.strReplaceAll('[antalanbefalinger]', intro, antalAnbefalinger.toString())) }
								</div>
							) }
							{ (
								<div className="result-contact">
									<a href={ bookingLink }>
										<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M9 2C9.55228 2 10 2.44772 10 3V4H14V3C14 2.44772 14.4477 2 15 2C15.5523 2 16 2.44772 16 3V4H19C20.1046 4 21 4.89543 21 6V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V6C3 4.89543 3.89543 4 5 4H8V3C8 2.44772 8.44772 2 9 2ZM8 6H5V9H19V6H16V7C16 7.55228 15.5523 8 15 8C14.4477 8 14 7.55228 14 7V6H10V7C10 7.55228 9.55228 8 9 8C8.44772 8 8 7.55228 8 7V6ZM19 11H5V19H19V11Z" fill="#92004D" />
										</svg>
										<p>{ bookingText }</p>
									</a>
									<a href={ 'tel:' + kontaktOsTextReplaced }>
										<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M2.15252 2H6.64247L8.08968 5.61803L5.7648 7.16795C5.4866 7.35342 5.3195 7.66565 5.3195 8C5.32204 8.09363 5.3195 8.001 5.3195 8.001L5.3195 8.00205L5.31951 8.00429L5.31954 8.00933L5.31969 8.02167C5.31983 8.03089 5.32009 8.04208 5.32051 8.05519C5.32135 8.0814 5.32288 8.1153 5.32562 8.15636C5.3311 8.23846 5.34141 8.34944 5.3608 8.48517C5.39956 8.75646 5.47478 9.12811 5.62082 9.56623C5.91405 10.4459 6.49023 11.5849 7.61239 12.7071C8.73456 13.8293 9.87357 14.4054 10.7533 14.6987C11.1914 14.8447 11.563 14.9199 11.8343 14.9587C11.9701 14.9781 12.081 14.9884 12.1631 14.9939C12.2042 14.9966 12.2381 14.9981 12.2643 14.999C12.2774 14.9994 12.2886 14.9997 12.2978 14.9998L12.3102 15L12.3152 15L12.3175 15L12.3185 15C12.3185 15 12.4301 14.9939 12.3195 15C12.6983 15 13.0445 14.786 13.2139 14.4472L13.8836 13.1078L18.3195 13.8471V18.167C16.2084 18.4723 10.5069 18.7732 6.02661 14.2929C1.54631 9.81259 1.84722 4.1111 2.15252 2ZM7.39315 8.48609L9.19908 7.28213C9.98601 6.75751 10.2979 5.75338 9.94663 4.87525L8.49942 1.25722C8.19569 0.497902 7.46028 0 6.64247 0H2.10028C1.19177 0 0.336588 0.631162 0.187443 1.6169C-0.151866 3.8595 -0.613714 10.481 4.61239 15.7071C9.8385 20.9332 16.46 20.4714 18.7026 20.1321C19.6883 19.9829 20.3195 19.1277 20.3195 18.2192V13.8471C20.3195 12.8694 19.6127 12.0351 18.6483 11.8743L14.2124 11.135C13.3464 10.9907 12.4874 11.4281 12.0948 12.2134L11.7483 12.9064C11.6394 12.8796 11.5178 12.8453 11.3857 12.8013C10.7654 12.5946 9.90444 12.1707 9.02661 11.2929C8.14877 10.4151 7.72495 9.55407 7.51818 8.93377C7.46231 8.76616 7.42209 8.61545 7.39315 8.48609Z" fill="#92004D" />
										</svg>
										<p>{ kontaktOsTextReplaced }</p>
									</a>
								</div>
							) }
						</div>
						{ erSagsbehandler && sagsbehandlerInfoTekst && (
							<div className="sagsbehandler-content">
								{ parse(sagsbehandlerInfoTekst) }
							</div>
						) }
						{ windowWidth < 1400 ? (
							<div className="recommendations-nav">
								<button
									className="btn-arrow left"
									onClick={ () => {
										handleCarouselClick('prev');
									} }
									disabled={ positionX === 0 || positionX === null }>
									<svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M1.53201 1.66721L6.91949 7.05469L1.53201 12.4422" stroke="#92004D" strokeWidth="2" />
									</svg>
								</button>
								<button
									className="btn-arrow"
									onClick={ () => {
										handleCarouselClick('next');
									} }
									disabled={ disableNext }>
									<svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M1.53201 1.66721L6.91949 7.05469L1.53201 12.4422" stroke="#92004D" strokeWidth="2" />
									</svg>
								</button>
							</div>
						) : null }
						<div className="recommendations-container" style={ windowWidth < 1400 ? { width: '100vw' } : {} }>
							<div
								className={ windowWidth < 1400 ? 'recommendations carousel' : 'recommendations' }
								style={ { transform: `translateX(${positionX}px)` } }
								{ ...handlers }
							>
								{ anbefalinger?.map(anbefaling => (
									<React.Fragment key={ anbefaling.id }>
										{ anbefaling.visRoedBaggrund && visiblity && (
											<div className="recommendation recom-red" key={ anbefaling.id }>
												<svg width="300" height="380" viewBox="0 0 300 380" fill="none" xmlns="http://www.w3.org/2000/svg">
													<circle opacity="0.1" cx="11" cy="276" r="195" fill="#CE0060" />
													<circle opacity="0.1" cx="242.5" cy="56.5" r="84.5" fill="#CE0060" />
												</svg>
												<div className="recommendation-content">
													{ anbefaling.text && <p>{ anbefaling.text }</p> }
													<div className="btn-container">
														<a
															href={ anbefaling.buttonLink1 }
															className="btn btn-secondary"
															target="_blank"
															rel="noreferrer">{ anbefaling.buttonText1 }</a>
														<a
															className="btn"
															onClick={ () => {
																setVisibility(false);
															} }
														>{ anbefaling.buttonText2 }</a>
													</div>
												</div>
											</div>
										) }
										{ !anbefaling.visRoedBaggrund && (
											<div className="recommendation" key={ anbefaling.id }>
												<div
													className="recommendation-content"
													onClick={ () => {
														handleModalClick(anbefaling.id.toString());
													} }>
													<img src={ anbefaling.icon } alt="ikon" className="recommendation-icon" />
													{ anbefaling.title && <h4>{ anbefaling.title }</h4> }
													{ anbefaling.text && <p>{ anbefaling.text }</p> }
													<button className="btn-arrow">
														<svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M1.53201 1.66721L6.91949 7.05469L1.53201 12.4422" stroke="#92004D" strokeWidth="2" />
														</svg>
													</button>
												</div>
												{ showModal && Number(modalId) === Number(anbefaling.id) && (
													<RecommendationModal
														modalIcon={ anbefaling.icon }
														modalHeader={ anbefaling.title }
														modalOverskrift={ anbefaling.modalOverskrift }
														modalText={ anbefaling.modalText }
														btn_link1={ anbefaling.buttonLink1 }
														btn_text1={ anbefaling.buttonText1 }
														btn_link2={ anbefaling.buttonLink2 }
														btn_text2={ anbefaling.buttonText2 }
														modalFooter={ anbefaling.modalFooter }
														graphic={ anbefaling.graphic }
														anbefalingsBaggrund={ anbefaling.anbefalingsBaggrund }
														visAnbefalingSamtykke={ anbefaling.visAnbefalingSamtykke }
														visRoedBaggrund={ anbefaling.visRoedBaggrund }
														samtykkeLabel={ samtykkeLabel }
														samtykkeTekst={ samtykkeTekst }
														samtykke={ samtykke }
														email={ email }
														telefon={ telefon }
														blockId={ blockId }
														landlinephone={ landlinephone }
														onClose={ ()=>setShowModal(false) }
													/>
												) }
											</div>
										) }
									</React.Fragment>
								)) }
							</div>
						</div>
						<div className="dots-container">
							{ anbefalinger.map((e: { id: React.Key }, i: number) => <div className="dot" key={ e.id } style={ Number(dotBg) === Number(i) ? { backgroundColor: '#CE0060' } : {} } />) }
						</div>
					</div>
				</Grid>
				{ !erSagsbehandler && renderPDF() }
			</main>
		</>
	);
};

export default ServicetjekResultat;
